import { useQuery } from "@tanstack/react-query";
import type { Approval, AuditCategory, AuditOutcome } from "@zworks/console-contracts";
import * as React from "react";
import { EmptyState, ErrorState, ForbiddenState, LoadingState } from "@/components/states";
import { PageHeader, StatusBadge } from "@/components/bespoke";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ApiError, approvalsInboxQuery, auditListQuery } from "@/lib/api";
import { formatDate, formatDateTime } from "@/lib/format";
import { useCurrentProject } from "@/lib/project";
import { ApprovalThread } from "./inbox-thread";
import { ApprovalStateBadge } from "@/components/inbox-badge";

type InboxSection = "approvals" | "notifications";
type QueueFilter = "waitingForMe" | "waitingForOthers" | "decided";

const queueTabs: { value: QueueFilter; label: string }[] = [
  { value: "waitingForMe", label: "Waiting for me" },
  { value: "waitingForOthers", label: "Waiting for others" },
  { value: "decided", label: "Decided" },
];

const categoryLabels: Record<AuditCategory, string> = {
  security: "Security",
  approvals: "Approvals",
  members: "Members",
  credits: "Credits",
  runs: "Runs",
};

/** One row of the system notifications tab, read from the project audit log. */
function NotificationRow({
  category,
  event,
  outcome,
  actor,
  createdAt,
}: {
  category: AuditCategory;
  event: string;
  outcome: AuditOutcome;
  actor: string | null;
  createdAt: string;
}) {
  return (
    <li className="flex flex-col gap-1 border-b px-4 py-3 last:border-b-0 sm:flex-row sm:items-center sm:gap-3">
      <Badge variant="secondary">{categoryLabels[category]}</Badge>
      <div className="min-w-0 flex-1">
        <p className="text-sm">{event}</p>
        <p className="text-[0.8125rem] text-muted-foreground tabular-nums">
          {formatDateTime(createdAt)}
          {actor ? ` · ${actor}` : ""}
        </p>
      </div>
      <StatusBadge kind="audit" status={outcome} />
    </li>
  );
}

function QueueButton({
  entry,
  selected,
  onSelect,
}: {
  entry: Approval;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      aria-current={selected}
      onClick={onSelect}
      className={
        selected
          ? "w-full border-b px-4 py-3 text-left last:border-b-0 bg-muted/60 shadow-[inset_3px_0_0_var(--color-primary)]"
          : "w-full border-b px-4 py-3 text-left last:border-b-0 hover:bg-muted/50"
      }
    >
      <span className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{entry.title}</span>
        <ApprovalStateBadge state={entry.status} />
      </span>
      <span className="mt-1 block font-mono text-xs text-muted-foreground">
        {entry.actionDigest} · rev {entry.revision} · {entry.requestedBy} ·{" "}
        {formatDate(entry.requestedAt)}
      </span>
    </button>
  );
}

/**
 * The approvals queue: two columns plus a system notifications tab. The left column is
 * the queue; the right column is the selected request as a working thread, in the manner of
 * the Claude desktop permission view. Eligibility arrives from the server with every row.
 */
export function ApprovalsPage() {
  const { project, loaded } = useCurrentProject();
  const [section, setSection] = React.useState<InboxSection>("approvals");
  const [queue, setQueue] = React.useState<QueueFilter>("waitingForMe");
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [filter, setFilter] = React.useState("");
  const inbox = useQuery({
    ...approvalsInboxQuery(project?.id ?? ""),
    enabled: loaded && project !== null,
  });
  const notifications = useQuery({
    ...auditListQuery(project?.id ?? "", "all", 1),
    enabled: loaded && project !== null && section === "notifications",
  });

  const heading = <PageHeader title="Approvals" />;

  // A disabled query stays pending, so the empty project is decided before the query.
  if (!loaded || (project !== null && section === "approvals" && inbox.isPending)) {
    return (
      <>
        {heading}
        <LoadingState label="Loading approvals" rows={4} />
      </>
    );
  }

  if (project === null) {
    return (
      <>
        {heading}
        <EmptyState
          title="No projects yet"
          description="Create your first project. Its approvals appear here."
        />
      </>
    );
  }

  if (section === "approvals" && inbox.isError) {
    const error = inbox.error;
    return (
      <>
        {heading}
        {error instanceof ApiError && error.status === 403 ? (
          <ForbiddenState
            title="You do not have access to this project"
            description="Ask an owner to invite you. Nothing from the project is shown."
          />
        ) : (
          <ErrorState
            title="Approvals could not be loaded"
            description={
              error instanceof ApiError
                ? (error.problem?.detail ?? error.message)
                : "The request could not be completed."
            }
            requestId={error instanceof ApiError ? (error.requestId ?? "unknown") : "unknown"}
            onRetry={() => void inbox.refetch()}
          />
        )}
      </>
    );
  }

  const data = inbox.data;
  const rows = (data?.[queue] ?? []).filter((entry) =>
    filter.trim()
      ? `${entry.title} ${entry.actionType} ${entry.requestedBy}`
          .toLowerCase()
          .includes(filter.trim().toLowerCase())
      : true,
  );
  const selected: Approval | undefined =
    rows.find((entry) => entry.id === selectedId) ??
    data?.[queue].find((entry) => entry.id === selectedId) ??
    rows[0];
  const totalPending = (data?.waitingForMe.length ?? 0) + (data?.waitingForOthers.length ?? 0);

  return (
    <div className="flex flex-col gap-6">
      {heading}

      <Tabs value={section} onValueChange={(value) => setSection(value as InboxSection)}>
        <TabsList aria-label="Approvals sections" className="max-w-full flex-wrap sm:flex-nowrap">
          <TabsTrigger value="approvals" className="h-11 px-2 sm:h-7.5 sm:px-3">
            Approvals · {totalPending}
          </TabsTrigger>
          <TabsTrigger value="notifications" className="h-11 px-2 sm:h-7.5 sm:px-3">
            Notifications
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {section === "notifications" ? (
        <Card>
          {notifications.isPending ? (
            <LoadingState label="Loading notifications" rows={4} />
          ) : notifications.isError ? (
            <ErrorState
              title="Notifications could not be loaded"
              description={
                notifications.error instanceof ApiError
                  ? (notifications.error.problem?.detail ?? notifications.error.message)
                  : "The request could not be completed."
              }
              requestId={
                notifications.error instanceof ApiError
                  ? (notifications.error.requestId ?? "unknown")
                  : "unknown"
              }
              onRetry={() => void notifications.refetch()}
            />
          ) : (notifications.data?.entries.length ?? 0) === 0 ? (
            <EmptyState
              title="No system notifications"
              description="Approvals, runs, credits, and security events appear here, newest first."
            />
          ) : (
            <ul aria-label="System notifications, newest first">
              {(notifications.data?.entries ?? []).map((entry) => (
                <NotificationRow
                  key={entry.id}
                  category={entry.category}
                  event={entry.event}
                  outcome={entry.outcome}
                  actor={entry.actorName}
                  createdAt={entry.createdAt}
                />
              ))}
            </ul>
          )}
        </Card>
      ) : !data ? (
        <LoadingState label="Loading approvals" rows={4} />
      ) : (
        <div className="flex flex-col gap-4">
          <Tabs
            value={queue}
            onValueChange={(value) => {
              setQueue(value as QueueFilter);
              setSelectedId(null);
            }}
          >
            <TabsList aria-label="Filter approvals" className="max-w-full flex-wrap sm:flex-nowrap">
              {queueTabs.map((entry) => (
                <TabsTrigger
                  key={entry.value}
                  value={entry.value}
                  className="h-11 px-2 sm:h-7.5 sm:px-3"
                >
                  {entry.label} · {data[entry.value].length}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          {rows.length === 0 ? (
            <EmptyState
              title={
                queue === "waitingForMe"
                  ? "No approvals waiting"
                  : queue === "waitingForOthers"
                    ? "Nothing waiting on others"
                    : "No decided approvals"
              }
              description={
                queue === "decided"
                  ? "Decided approvals appear here with who decided them."
                  : "Nothing is waiting for a decision. New requests appear here."
              }
            />
          ) : (
            <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]">
              <Card aria-label="Approval queue">
                <div className="border-b p-3">
                  <label className="sr-only" htmlFor="inbox-filter">
                    Filter queue
                  </label>
                  <input
                    id="inbox-filter"
                    value={filter}
                    onChange={(event) => setFilter(event.target.value)}
                    placeholder="Filter queue…"
                    className="h-9 w-full rounded-md border border-input bg-card px-3 text-sm"
                  />
                </div>
                <div role="listbox" aria-label="Approvals, newest first">
                  {rows.map((entry) => (
                    <QueueButton
                      key={entry.id}
                      entry={entry}
                      selected={selected?.id === entry.id}
                      onSelect={() => setSelectedId(entry.id)}
                    />
                  ))}
                </div>
              </Card>

              <div className="min-w-0">
                {selected ? (
                  <ApprovalThread projectId={project.id} entry={selected} />
                ) : (
                  <EmptyState
                    title="Select a request"
                    description="Choose a row in the queue to review it here."
                  />
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
